import React, { useMemo } from 'react';
import cn from 'classnames';
import Container from 'components/UI/Container';
import { Grid, GridItem } from 'components/UI/Grid';
import NavColumn from './components/NavColumn';
import Link from 'components/UI/Link';
import Contacts from 'components/UI/Contacts';
import OfficeCard from './components/OfficeCard';
import Accordion from './components/Accordion';
import Copyright from './components/Copyright';
import TopScroller from './components/TopScroller/TopScroller';
import { arabicRegion, azerRegion, germanRegion, americasRegion, ukRegion } from 'define/locale';
import { isRequestedFrom, useLang, useTrans } from 'utils/localisation';
import { useSsr } from 'utils/hooks';
import { useOfficesInfo } from 'staticQueries/offices-info';
import { useFooterTranslation } from 'staticQueries/footer-translation';

import * as styles from './Footer.module.scss';
import data from './data.json';

const {
  offices,
  officesDe,
  officesUS,
  officesMena,
  officesAz,
  officesNl,
  officesKz,
  officesIn,
  officesUk,
  officesFr,
} = data;

export default function Footer() {
  const officesData = useOfficesInfo();
  const { techStack, industries, services, projects, company } = useFooterTranslation();
  const isSsr = useSsr();

  const t = useTrans(['common', 'footer']);
  const lang = useLang();

  const getOfficesConfig = () => {
    const officesMapping = [
      {
        condition: () => lang === 'de' || isRequestedFrom(germanRegion),
        offices: officesDe,
      },
      {
        condition: () => lang === 'ae' || isRequestedFrom(arabicRegion),
        offices: officesMena,
      },
      {
        condition: () => lang === 'az' || isRequestedFrom(azerRegion),
        offices: officesAz,
      },
      {
        condition: () => lang === 'fr' || isRequestedFrom('fr'),
        offices: officesFr,
      },
      {
        condition: () => lang === 'kz' || (lang === 'en' && isRequestedFrom('kz')),
        offices: officesKz,
      },
      {
        condition: () => lang === 'en' && isRequestedFrom('in'),
        offices: officesIn,
      },
      {
        condition: () => isRequestedFrom(americasRegion),
        offices: officesUS,
      },
      {
        condition: () => lang === 'uk' || (lang === 'en' && isRequestedFrom(ukRegion)),
        offices: officesUk,
      },
      {
        condition: () => lang === 'nl' || (lang === 'en' && isRequestedFrom('NL')),
        offices: officesNl,
      },
    ];

    const matchedConfig = officesMapping.find(config => config.condition());
    return matchedConfig ? matchedConfig.offices : offices;
  };

  const officesIds = useMemo(() => (isSsr ? offices : getOfficesConfig()), [lang, isSsr]);

  const officesList = useMemo(() => {
    const result = officesIds?.reduce((acc, locationItem) => {
      const office = officesData.find(office => office.id === locationItem);
      office && acc.push(office);
      return acc;
    }, []);

    return result;
  }, [lang, officesIds, officesData]);

  const officesLink =
    lang === 'az' || lang === 'nl'
      ? 'https://andersenlab.com/about-us#offices'
      : '/about-us#offices';

  return (
    <footer className={styles.footer}>
      <Container background="transparent" padding="Vertical">
        <div className={styles.main}>
          <div className={styles.accordionLayout}>
            <Accordion title={t('techStack')}>
              <NavColumn items={techStack} />
            </Accordion>
            <Accordion title={t('industries')}>
              <NavColumn items={industries} />
            </Accordion>
            <Accordion title={t('services')}>
              <NavColumn items={services} />
            </Accordion>
            <Accordion title={t('caseStudies')}>
              <NavColumn items={projects} />
            </Accordion>
            <Accordion title={t('company')}>
              <NavColumn items={company} />
            </Accordion>
          </div>
          <section className={cn(styles.section, styles.navigation)}>
            <NavColumn title={t('techStack')} items={techStack} />
            <NavColumn title={t('industries')} items={industries} />
            <NavColumn title={t('services')} items={services} />
            <NavColumn title={t('caseStudies')} items={projects} />
            <NavColumn title={t('company')} items={company} />
          </section>
          <section className={cn(styles.section, styles.offices)}>
            <p className={styles.title}>{t('ourOffices')}</p>
            <div className={styles.sectionContent}>
              <Grid
                gutterX="20px"
                xsm={{ cols: 2 }}
                sm={{ cols: 2 }}
                md={{ cols: 4 }}
                lg={{ cols: 4 }}
                xl={{ cols: 4 }}
              >
                {officesList?.map(item => {
                  return (
                    <GridItem key={item.id}>
                      <OfficeCard {...item} />
                    </GridItem>
                  );
                })}
              </Grid>
              <div className={styles.buttonRow}>
                <Link href={officesLink} className={styles.button}>
                  {t('seeMore')}
                </Link>
              </div>
            </div>
          </section>
          <section className={cn(styles.section, styles.contactUs)}>
            <p className={styles.title}>{t('contactUs')}</p>
            <div className={styles.sectionContent}>
              <Contacts className={styles.column} blockLocation="footer" />
              <TopScroller />
            </div>
          </section>
        </div>
      </Container>
      <Copyright />
    </footer>
  );
}
